/**
 * Hàm thuần của trang "Lịch phát thanh": giờ theo múi giờ Việt Nam (+07:00), thời
 * lượng khung giờ theo bản tin, kiểm lỗi và gom khung giờ thành các lịch để lưu.
 *
 * Mô hình máy chủ: MỘT lịch phát có MỘT danh sách bản tin dùng cho mọi khung giờ của nó
 * (đúng khuôn DV002: `DanhSachNgayPhat` + `DanhSachBanTin`). Trang cho mỗi khung giờ một
 * danh sách bản tin riêng, nên khi lưu các khung giờ CÙNG danh sách bản tin được gom
 * thành một lịch.
 */
import type { DiaBanNhanDV, LichPhatNoiBoRa, LichPhatNoiBoVao } from '../api/lich-phat-api';
import { dienVao } from '../ngon-ngu/vi';
import { viLichPhatThanh } from '../ngon-ngu/vi-lich-phat-thanh';

const L = viLichPhatThanh.loi;

/** Múi giờ Việt Nam: người vận hành nhập giờ địa phương. */
export const LECH_VN_GIAY = 7 * 3600;
export const MOT_NGAY_GIAY = 86400;

export interface KhungGioSoan {
  /** Khoá ổn định cho React. */
  id: string;
  /** Giây tính từ 00:00 giờ Việt Nam. */
  batDau: number;
  /** Giây tính từ 00:00; `null` = tự tính theo thời lượng bản tin. */
  ketThucTay: number | null;
  /** Mã nghiệp vụ bản tin (`banTinId`), đúng thứ tự phát. */
  banTin: string[];
}

export interface NgayPhatSoan {
  /** `YYYY-MM-DD` theo lịch Việt Nam. */
  ngay: string;
  khung: KhungGioSoan[];
}

/** `HH:mm:ss` → giây; sai định dạng trả `null`. */
export function giayTuHms(s: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec((s ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const p = Number(m[2]);
  const g = Number(m[3] ?? 0);
  if (h > 23 || p > 59 || g > 59) return null;
  return h * 3600 + p * 60 + g;
}

/** Giây → `HH:mm:ss` (cho phép vượt 24 giờ khi là thời lượng). */
export function hmsTuGiay(giay: number): string {
  const s = Math.max(0, Math.round(giay));
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
}

/** Ngày hôm nay theo giờ Việt Nam, `YYYY-MM-DD`. */
export function homNayVn(bayGioMs: number = Date.now()): string {
  return new Date(bayGioMs + LECH_VN_GIAY * 1000).toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` → `DD-MM-YYYY` (hiển thị). */
export function hienThiNgay(ngay: string): string {
  const [y, m, d] = ngay.split('-');
  return `${d}-${m}-${y}`;
}

/** Epoch giây của `ngay` + `giay` (giờ Việt Nam). */
export function epochVn(ngay: string, giay: number): number {
  const [y, m, d] = ngay.split('-').map(Number);
  return Date.UTC(y as number, (m as number) - 1, d as number) / 1000 + giay - LECH_VN_GIAY;
}

/** Epoch 00:00 UTC của ngày lịch (khuôn `NgayPhat` máy chủ dùng để gom theo ngày). */
export function epochNgay(ngay: string): number {
  const [y, m, d] = ngay.split('-').map(Number);
  return Date.UTC(y as number, (m as number) - 1, d as number) / 1000;
}

/** Tổng thời lượng (giây) của các bản tin; `null` khi có bản tin chưa rõ thời lượng. */
export function tongThoiLuong(
  banTin: string[],
  thoiLuongTheoMa: Record<string, number | null>,
): number | null {
  let tong = 0;
  for (const ma of banTin) {
    const g = thoiLuongTheoMa[ma];
    if (g === null || g === undefined) return null;
    tong += g;
  }
  return tong;
}

/** Giờ kết thúc (giây từ 00:00): tay nếu có, ngược lại bắt đầu + tổng thời lượng. */
export function ketThucKhung(k: KhungGioSoan, thoiLuongTheoMa: Record<string, number | null>): number | null {
  if (k.ketThucTay !== null) return k.ketThucTay;
  const tong = tongThoiLuong(k.banTin, thoiLuongTheoMa);
  return tong === null ? null : k.batDau + tong;
}

/** Tên khung giờ tự sinh: "Khung giờ 05:00:00 - 05:05:13". */
export function tenKhung(batDau: number, ketThuc: number | null): string {
  return dienVao(viLichPhatThanh.tenKhung, { bd: hmsTuGiay(batDau), kt: hmsTuGiay(ketThuc ?? batDau) });
}

/** Lỗi của bản soạn; rỗng = lưu được. */
export function kiemLoiLich(
  dsNgay: NgayPhatSoan[],
  thoiLuongTheoMa: Record<string, number | null>,
  bayGioGiay: number,
): string[] {
  const loi: string[] = [];
  if (dsNgay.length === 0) loi.push(L.chuaCoNgay);
  for (const n of dsNgay) {
    const nhan = hienThiNgay(n.ngay);
    if (n.khung.length === 0) loi.push(dienVao(L.ngayChuaCoKhung, { ngay: nhan }));
    const doan: { bd: number; kt: number; ten: string }[] = [];
    for (const k of n.khung) {
      const kt = ketThucKhung(k, thoiLuongTheoMa);
      const ten = `${nhan} ${tenKhung(k.batDau, kt)}`;
      if (k.banTin.length === 0) loi.push(dienVao(L.chuaChonBanTin, { khung: ten }));
      if (kt === null) loi.push(dienVao(L.thieuThoiLuong, { khung: ten }));
      else if (kt <= k.batDau) loi.push(dienVao(L.ketThucTruoc, { khung: ten }));
      else if (kt > MOT_NGAY_GIAY) loi.push(dienVao(L.qua24, { khung: ten }));
      if (epochVn(n.ngay, k.batDau) < bayGioGiay) loi.push(dienVao(L.quaKhu, { khung: ten }));
      if (kt !== null) doan.push({ bd: k.batDau, kt, ten });
    }
    doan.sort((a, b) => a.bd - b.bd);
    for (let i = 1; i < doan.length; i++) {
      const truoc = doan[i - 1] as { bd: number; kt: number; ten: string };
      const sau = doan[i] as { bd: number; kt: number; ten: string };
      if (sau.bd < truoc.kt) {
        loi.push(dienVao(L.trung, { khung: sau.ten, khac: truoc.ten.slice(nhan.length + 1) }));
      }
    }
  }
  return loi;
}

/**
 * Gom bản soạn thành các thân `POST /api/v1/schedules`: mỗi danh sách bản tin (đúng thứ
 * tự) một lịch, gồm mọi ngày/khung giờ dùng danh sách đó. Gọi SAU `kiemLoiLich`.
 */
export function gomThanhLich(
  tenLichPhat: string,
  dsNgay: NgayPhatSoan[],
  phamVi: DiaBanNhanDV[],
  thoiLuongTheoMa: Record<string, number | null>,
): LichPhatNoiBoVao[] {
  const nhom = new Map<string, Map<string, { ThoiGianBatDau: number; ThoiGianKetThuc: number }[]>>();
  for (const n of [...dsNgay].sort((a, b) => a.ngay.localeCompare(b.ngay))) {
    for (const k of [...n.khung].sort((a, b) => a.batDau - b.batDau)) {
      const kt = ketThucKhung(k, thoiLuongTheoMa) ?? k.batDau;
      const khoa = k.banTin.join('|');
      const theoNgay = nhom.get(khoa) ?? new Map();
      const ds = theoNgay.get(n.ngay) ?? [];
      ds.push({ ThoiGianBatDau: epochVn(n.ngay, k.batDau), ThoiGianKetThuc: epochVn(n.ngay, kt) });
      theoNgay.set(n.ngay, ds);
      nhom.set(khoa, theoNgay);
    }
  }
  const soNhom = nhom.size;
  return [...nhom].map(([khoa, theoNgay], i) => ({
    tenLichPhat: soNhom > 1 ? `${tenLichPhat} (${i + 1}/${soNhom})` : tenLichPhat,
    banTinIds: khoa.split('|'),
    phamVi,
    loaiLich: 'theo_ngay',
    danhSachNgayPhat: [...theoNgay].map(([ngay, ThoiDiemPhat]) => ({
      NgayPhat: epochNgay(ngay),
      ThoiDiemPhat,
    })),
  }));
}

/** Lưới lịch tháng bắt đầu Chủ Nhật: mảng tuần, ô trống là `null`. */
export function luoiThang(nam: number, thang: number): (string | null)[][] {
  const dau = new Date(Date.UTC(nam, thang - 1, 1));
  const soNgay = new Date(Date.UTC(nam, thang, 0)).getUTCDate();
  const o: (string | null)[] = Array.from({ length: dau.getUTCDay() }, () => null);
  for (let d = 1; d <= soNgay; d++) {
    o.push(`${nam}-${String(thang).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  while (o.length % 7 !== 0) o.push(null);
  const tuan: (string | null)[][] = [];
  for (let i = 0; i < o.length; i += 7) tuan.push(o.slice(i, i + 7));
  return tuan;
}

/**
 * Lịch đã lưu → bản soạn để SỬA (nút "Chỉnh sửa" ở danh sách lịch). Chỉ giữ khung giờ CHƯA
 * bắt đầu (khung đã qua không sửa được và máy chủ từ chối giờ quá khứ); lịch đã hết hiệu lực
 * thì bản soạn không còn ngày nào, người dùng thêm ngày mới. Mọi khung dùng chung danh sách
 * bản tin của lịch (mô hình máy chủ một lịch một danh sách bản tin).
 */
export function soanTuLich(
  l: Pick<LichPhatNoiBoRa, 'banTinIds' | 'danhSachNgayPhat'>,
  bayGioGiay: number,
  taoId: () => string,
): NgayPhatSoan[] {
  const theoNgay = new Map<string, KhungGioSoan[]>();
  for (const n of l.danhSachNgayPhat) {
    for (const tp of n.ThoiDiemPhat) {
      if (tp.ThoiGianBatDau < bayGioGiay) continue;
      const ngay = homNayVn(tp.ThoiGianBatDau * 1000);
      const dauNgay = epochVn(ngay, 0);
      const ds = theoNgay.get(ngay) ?? [];
      ds.push({
        id: taoId(),
        batDau: tp.ThoiGianBatDau - dauNgay,
        ketThucTay: tp.ThoiGianKetThuc - dauNgay,
        banTin: [...l.banTinIds],
      });
      theoNgay.set(ngay, ds);
    }
  }
  return [...theoNgay]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ngay, khung]) => ({ ngay, khung: khung.sort((a, b) => a.batDau - b.batDau) }));
}
